import React, { useState } from "react";
import { useI18n } from "@/i18n";
import { useAuth } from "@/auth";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Building2, ClipboardCheck, Coffee, Droplet, Sparkles, Users, ShieldCheck, LayoutDashboard } from "lucide-react";
import { useAdmin } from "@/hooks/useAdmin";
import Seo from "@/components/Seo";
import OverviewTab from "@/components/mycafe/OverviewTab";
import BranchesTab from "@/components/mycafe/BranchesTab";
import VisitsTab from "@/components/mycafe/VisitsTab";
import EspressoTab from "@/components/mycafe/EspressoTab";
import DripTab from "@/components/mycafe/DripTab";
import CustomTab from "@/components/mycafe/CustomTab";
import TeamTab from "@/components/mycafe/TeamTab";
import AdminUsageTab from "@/components/mycafe/AdminUsageTab";

export default function MyCafe() {
  const { isRTL } = useI18n();
  const { user } = useAuth();
  const isAdmin = useAdmin();
  const [tab, setTab] = useState("overview");

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-10 lg:pt-16 pb-24" data-testid="mycafe-page">
      <Seo
        title={isRTL ? "مقهاي — مساحة خاصة لإدارة مقاهيك" : "My Cafe — Private Cafe Owner Workspace"}
        description={isRTL
          ? "مساحة خاصة بصاحب المقهى: تقارير زيارات الجودة، وصفات الإسبريسو الخاصة، قهوة اليوم، مشاركة الموظفين، وتصدير PDF."
          : "Private cafe owner workspace: quality visit reports, espresso & drip recipes, employee sharing, PDF export."}
        path="/my-cafe"
      />

      <div className="flex flex-wrap items-end justify-between gap-4 mb-2">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-[#C86240] mb-3">
            {isRTL ? "خاص بك · لا أحد يرى الوصفات" : "Private workspace · zero public visibility"}
          </div>
          <h1 className={`${isRTL ? "font-serif-ar" : "font-serif-en"} text-4xl lg:text-6xl text-[#2A2421] leading-[1.02]`}>
            {isRTL ? "مقهاي" : "My Cafe"}
          </h1>
          <p className="mt-4 text-[#5C544F] max-w-2xl">
            {isRTL
              ? `مرحباً ${user?.name || ""} — هنا تدير فروعك، تقارير زيارات الجودة، وصفاتك الخاصة (إسبريسو · قهوة اليوم · مشروبات مخصصة) ومشاركتها مع موظفيك. لا تظهر هذه البيانات لأي مستخدم آخر في المنصة.`
              : `Welcome ${user?.name || ""} — manage your branches, quality visit reports, private recipes (espresso · drip · custom drinks) and share them with your staff. Nothing here is visible to other users on the platform.`}
          </p>
        </div>
        <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#F1E9DA] text-[#2A2421] text-xs">
          <ShieldCheck className="w-3.5 h-3.5 text-[#2F8C5C]" />
          {isRTL ? "محمي بحسابك فقط" : "Locked to your account"}
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="mt-10">
        <div className="overflow-x-auto -mx-2 px-2 pb-1">
          <TabsList className="bg-[#F9F6F0] border border-[#2A2421]/10 rounded-full p-1 h-auto inline-flex flex-nowrap whitespace-nowrap">
            <TabTrigger v="overview" icon={LayoutDashboard} label={isRTL ? "نظرة عامة" : "Overview"} />
            <TabTrigger v="branches" icon={Building2} label={isRTL ? "الفروع" : "Branches"} />
            <TabTrigger v="visits" icon={ClipboardCheck} label={isRTL ? "زيارات الجودة" : "Quality Visits"} />
            <TabTrigger v="espresso" icon={Coffee} label={isRTL ? "إسبريسو" : "Espresso"} />
            <TabTrigger v="drip" icon={Droplet} label={isRTL ? "قهوة اليوم" : "Coffee of the Day"} />
            <TabTrigger v="custom" icon={Sparkles} label={isRTL ? "مشروبات مخصصة" : "Custom Drinks"} />
            <TabTrigger v="team" icon={Users} label={isRTL ? "الموظفون" : "Team"} />
            {isAdmin && <TabTrigger v="admin" icon={ShieldCheck} label={isRTL ? "إحصاءات (Admin)" : "Usage (Admin)"} />}
          </TabsList>
        </div>

        <TabsContent value="overview" className="mt-8"><OverviewTab onGoTo={setTab} /></TabsContent>
        <TabsContent value="branches" className="mt-8"><BranchesTab /></TabsContent>
        <TabsContent value="visits" className="mt-8"><VisitsTab /></TabsContent>
        <TabsContent value="espresso" className="mt-8"><EspressoTab /></TabsContent>
        <TabsContent value="drip" className="mt-8"><DripTab /></TabsContent>
        <TabsContent value="custom" className="mt-8"><CustomTab /></TabsContent>
        <TabsContent value="team" className="mt-8"><TeamTab /></TabsContent>
        {isAdmin && <TabsContent value="admin" className="mt-8"><AdminUsageTab /></TabsContent>}
      </Tabs>
    </div>
  );
}

const TabTrigger = ({ v, icon: Icon, label }) => (
  <TabsTrigger
    value={v}
    data-testid={`mycafe-tab-${v}`}
    className="rounded-full px-4 py-2 text-xs data-[state=active]:bg-[#2A2421] data-[state=active]:text-[#F9F6F0]"
  >
    <Icon className="w-3.5 h-3.5 me-1.5" />
    {label}
  </TabsTrigger>
);
